import React from 'react';
import { Button, Box, Stack, Typography, Checkbox } from '@mui/material';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useMutation } from 'react-fetching-library';
import { DebounceInput } from 'react-debounce-input';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

import { SmallFormTextField } from '../../../../../components/small-fields/SmallFormTextField';
import { AlertDialogContainer } from '../../../../../components/others/AlertDialogContainer';
import { submitPeriodAction } from '../../../../../services/request';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { useSelector } from 'react-redux';
import { SmallFormDateField } from '../../../../../components/small-fields/SmallFormDateField';
import { formatDate } from '../../../../../services/functions';

export const PeriodForm = props => {
  const { data, submitCallback, handleClose, allResorts, allSeasons } = props;
  const { seasonIdentifier, module, seasonYear } = useSelector(state => state.app);
  const { loading, mutate } = useMutation(submitPeriodAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false);

  const validationSchema = Yup.object({
    name: Yup.string().required('To pole nie może być puste'),
    days_count: Yup.number().required('To pole nie może być puste'),
    is_weekend: Yup.boolean(),
    main_activities_count: Yup.number().required('To pole nie może być puste'),
    rope_activities_count: Yup.number().required('To pole nie może być puste'),
    educational_activities_count: Yup.number().required('To pole nie może być puste'),
    educational_trips_count: Yup.number().required('To pole nie może być puste')
      .default(1),
    gross_value: Yup.number().required('To pole nie może być puste')
  });

  const initialValues = {
    name: data ? data.name || '' : '',
    days_count: data ? data.days_count || 0 : 0,
    is_weekend: data ? data.is_weekend : false,
    main_activities_count: data ? data.main_activities_count || 0 : 0,
    rope_activities_count: data ? data.rope_activities_count || 0 : 0,
    educational_activities_count: data ? data.educational_activities_count || 0 : 0,
    educational_trips_count: data ? data.educational_trips_count || 1 : 1,
    gross_value: data ? data.gross_value || 0 : 0,
    resorts: data ? data.resorts : [],
    season: data ? data.season : '',
    estimated_total_cost_per_participant: data ? data.estimated_total_cost_per_participant : 0,
    entertainment_activities_count: data ? data.entertainment_activities_count || 0 : 0,
    integration_activities_count: data ? data.integration_activities_count || 0 : 0,
    block_activities_count: data ? data.block_activities_count || 0 : 0,
    catering_price_per_day_adults_13: data ? data.catering_price_per_day_adults_13 || 0 : 0,
    catering_price_per_day_kids_3_12: data ? data.catering_price_per_day_kids_3_12 || 0 : 0,
    animal_price_per_day: data ? data.animal_price_per_day || 0 : 0,
    date_from: data ? data.date_from : null,
    date_to: data ? data.date_to : null,
    wr_price_per_participant: data ? data.wr_price_per_participant : 0
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values, data ? 'PUT' : 'POST', data ? data.id : undefined);
    if (!error) {
      submitCallback(payload, data ? 'edit' : 'created', 'success', data ? 'Zaktualizowano termin' : 'Dodano nowy termin', '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle('Nie udało się dodać nowego terminu');
      setMessage('');
      setShowAlert(true);
    }
  };

  const handleDelete = async () => {
    const { error, payload } = await mutate([], 'DELETE', data.id);
    if (!error) {
      submitCallback(data, 'delete', 'success', `Usunięto termin ${data.name}`, '');
      handleClose();
    } else {
      setSeverity('error');
      setTitle(`Nie udało się usunąć terminu ${data.name}`);
      setMessage(payload);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <>
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ textAlign: 'center' }}>
        <Box display="flex" flexDirection="column" mt={1} p={1} ml="5%" mr="5%">
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Nazwa</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="name"
              id="name"
              type="text"
              placeholder="nazwa terminu"
              value={formik.values.name}
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
              debounceTimeout={100}
              element={SmallFormTextField}
              autoFocus
            />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ilość dni</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="days_count"
              id="days_count"
              type="text"
              placeholder="Ilość dni"
              value={formik.values.days_count}
              onChange={formik.handleChange}
              error={formik.touched.days_count && Boolean(formik.errors.days_count)}
              helperText={formik.touched.days_count && formik.errors.days_count}
              debounceTimeout={100}
              element={SmallFormTextField}
            />
          </Stack>

          {module == 'wyjazdy_rodzinne' && seasonYear == 2025 && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Cena wyżywienia dla dzieci powyżej 13 lat i dorosłych za 1 dobę</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="catering_price_per_day_adults_13"
              id="catering_price_per_day_adults_13"
              type="text"
              placeholder="koszt wyżywienia"
              value={formik.values.catering_price_per_day_adults_13}
              onChange={formik.handleChange}
              error={formik.touched.catering_price_per_day_adults_13 && Boolean(formik.errors.catering_price_per_day_adults_13)}
              helperText={formik.touched.catering_price_per_day_adults_13 && formik.errors.catering_price_per_day_adults_13}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module == 'wyjazdy_rodzinne' && seasonYear == 2025 && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Cena wyżywienia dla dzieci od 3 do 12 lat za 1 dobę</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="catering_price_per_day_kids_3_12"
              id="catering_price_per_day_kids_3_12"
              type="text"
              placeholder="koszt wyżywienia"
              value={formik.values.catering_price_per_day_kids_3_12}
              onChange={formik.handleChange}
              error={formik.touched.catering_price_per_day_kids_3_12 && Boolean(formik.errors.catering_price_per_day_kids_3_12)}
              helperText={formik.touched.catering_price_per_day_kids_3_12 && formik.errors.catering_price_per_day_kids_3_12}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module == 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Cena zwierzęcia za 1 dobę</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="animal_price_per_day"
              id="animal_price_per_day"
              type="text"
              placeholder="koszt zwierzęcia"
              value={formik.values.animal_price_per_day}
              onChange={formik.handleChange}
              error={formik.touched.animal_price_per_day && Boolean(formik.errors.animal_price_per_day)}
              helperText={formik.touched.animal_price_per_day && formik.errors.animal_price_per_day}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module == 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Data od</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_from"
              id="date_from"
              type="text"
              placeholder="date_from"
              value={formik.values.date_from}
              onChange={val => formik.setFieldValue('date_from', formatDate(val))}
              error={formik.touched.date_from && Boolean(formik.errors.date_from)}
              helperText={formik.touched.date_from && formik.errors.date_from}
            />
          </Stack>}
          {module == 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Data do</Typography>
            <SmallFormDateField
              sx={{ flex: '1 1 90%' }}
              name="date_to"
              id="date_to"
              type="text"
              placeholder="date_to"
              value={formik.values.date_to}
              onChange={val => formik.setFieldValue('date_to', formatDate(val))}
              error={formik.touched.date_to && Boolean(formik.errors.date_to)}
              helperText={formik.touched.date_to && formik.errors.date_to}
            />
          </Stack>}

          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="right" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 23%">Weekend</Typography>
            <Checkbox
              sx={{ flex: '1 1 75%' }}
              id="is_weekend"
              name="is_weekend"
              checked={formik.values.is_weekend}
              onChange={formik.handleChange} />
          </Stack>}
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">{ ['l_24_zs', 'l_25_zs'].includes(seasonIdentifier) ? 'Całkowita ilość zajęć do wyboru' : 'Ilość zajęć głównych'}</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="main_activities_count"
              id="main_activities_count"
              type="text"
              placeholder="ilość głównych zajęć"
              value={formik.values.main_activities_count}
              onChange={formik.handleChange}
              error={formik.touched.main_activities_count && Boolean(formik.errors.main_activities_count)}
              helperText={formik.touched.main_activities_count && formik.errors.main_activities_count}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ilość linowych zajęć</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="rope_activities_count"
              id="rope_activities_count"
              type="text"
              placeholder="ilość linowych zajęć"
              value={formik.values.rope_activities_count}
              onChange={formik.handleChange}
              error={formik.touched.rope_activities_count && Boolean(formik.errors.rope_activities_count)}
              helperText={formik.touched.rope_activities_count && formik.errors.rope_activities_count}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ilość edukacyjnych zajęć</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="educational_activities_count"
              id="educational_activities_count"
              type="text"
              placeholder="ilość edukacyjnych zajęć"
              value={formik.values.educational_activities_count}
              onChange={formik.handleChange}
              error={formik.touched.educational_activities_count && Boolean(formik.errors.educational_activities_count)}
              helperText={formik.touched.educational_activities_count && formik.errors.educational_activities_count}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ilość rozrywkowych zajęć</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="entertainment_activities_count"
              id="entertainment_activities_count"
              type="text"
              placeholder="ilość zajęć rozrywkowych"
              value={formik.values.entertainment_activities_count}
              onChange={formik.handleChange}
              error={formik.touched.entertainment_activities_count && Boolean(formik.errors.entertainment_activities_count)}
              helperText={formik.touched.entertainment_activities_count && formik.errors.entertainment_activities_count}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          { module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ilość integracyjnych zajęć</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="integration_activities_count"
              id="integration_activities_count"
              type="text"
              placeholder="ilość integracyjnych zajęć"
              value={formik.values.integration_activities_count}
              onChange={formik.handleChange}
              error={formik.touched.integration_activities_count && Boolean(formik.errors.integration_activities_count)}
              helperText={formik.touched.integration_activities_count && formik.errors.integration_activities_count}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module !== 'wyjazdy_rodzinne' && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ilość blokowych zajęć</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="block_activities_count"
              id="block_activities_count"
              type="text"
              placeholder="ilość blokowych zajęć"
              value={formik.values.block_activities_count}
              onChange={formik.handleChange}
              error={formik.touched.block_activities_count && Boolean(formik.errors.block_activities_count)}
              helperText={formik.touched.block_activities_count && formik.errors.block_activities_count}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}
          {module == 'wyjazdy_rodzinne' && seasonYear > 2025 && <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Cena per uczestnik za 1 dobę (zł)</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="wr_price_per_participant"
              id="wr_price_per_participant"
              type="text"
              placeholder="koszt imprezy"
              value={formik.values.wr_price_per_participant}
              onChange={formik.handleChange}
              error={formik.touched.wr_price_per_participant && Boolean(formik.errors.wr_price_per_participant)}
              helperText={formik.touched.wr_price_per_participant && formik.errors.wr_price_per_participant}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack>}


          {/* <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Szacunkowy koszt całkowity per uczestnik (zł)</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="estimated_total_cost_per_participant"
              id="estimated_total_cost_per_participant"
              type="text"
              placeholder="koszt imprezy"
              value={formik.values.estimated_total_cost_per_participant}
              onChange={formik.handleChange}
              error={formik.touched.estimated_total_cost_per_participant && Boolean(formik.errors.estimated_total_cost_per_participant)}
              helperText={formik.touched.estimated_total_cost_per_participant && formik.errors.estimated_total_cost_per_participant}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack> */}
          {/* <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 25%">Ilość wycieczek edukacyjnych</Typography>
            <DebounceInput
              sx={{ flex: '1 1 90%' }}
              name="educational_trips_count"
              id="educational_trips_count"
              type="text"
              placeholder="ilość wycieczek edukacyjnych"
              value={formik.values.educational_trips_count}
              onChange={formik.handleChange}
              error={formik.touched.educational_trips_count && Boolean(formik.errors.educational_trips_count)}
              helperText={formik.touched.educational_trips_count && formik.errors.educational_trips_count}
              debounceTimeout={300}
              element={SmallFormTextField}
            />
          </Stack> */}
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Ośrodki</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              multiple
              id="resorts"
              name="resorts"
              options={allResorts}
              value={formik.values.resorts}
              onChange={formik.handleChange} />
          </Stack>
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} mb={2}>
            <Typography variant="subtitle2" flex="1 1 10%">Sezon</Typography>
            <SmallFormTextField
              sx={{ flex: '1 1 90%' }}
              select
              id="season"
              name="season"
              options={allSeasons}
              value={formik.values.season}
              onChange={formik.handleChange} />
          </Stack>
        </Box>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          sx={{ mt: 3 }}
          disabled={loading}
        >
          <AddIcon />
          Zapisz
        </Button>
        { data
          ? <Button
            color="error"
            variant="contained"
            sx={{ mt: 3, ml: 4 }}
            onClick={() => {
              setConfirmDeleteOpen(true);
            }}
          >
            <DeleteIcon />
          Usuń
          </Button>
          : null}
      </form>
      <ConfirmDialog
        title="Usunąć termin?"
        open={confirmDeleteOpen}
        setOpen={setConfirmDeleteOpen}
        onConfirm={handleDelete}
        disabled={loading}
      >
        Czy na pewno chcesz usunąć ten termin?
      </ConfirmDialog>
    </>

  );
};
